import {useNavigation} from '@react-navigation/native';
import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  FlatList,
  Pressable,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableWithoutFeedback,
  Keyboard,
  ActivityIndicator,
  Alert,
  Modal,
} from 'react-native';
import Header from '../../components/Header/Header';
import {styles} from './styles';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import moment from 'moment-timezone';
import OrdersService from '../../services/OrdersService/OrdersService';
import {useAuthStore} from '../../../src/store/AuthStore';
import {useUserStore} from '../../store/UserStore';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import Toast from '../../components/Toast/Toast';
import {Colors} from '../../constants/Colors';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import {getAnalytics, logEvent} from '@react-native-firebase/analytics';
import { showInfo } from '../../utils/globalAlert';

const reasons = [
  'Não vou abrir na data de entrega',
  'Desisti do pedido',
  'Pedido duplicado',
  'Fiz o pedido errado',
  'Outro motivo',
];

const CancelOrder = ({route}) => {
  const {goBack} = useNavigation();
  const {item, canCancel} = route?.params;
  const {navigate} = useNavigation<any>();
  const [toast, setToast] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const analyticsInstance = getAnalytics();
  const {clientCode} = useAuthStore().user;
  const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);

  const [deliveryDate, setDeliveryDate] = useState('');
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReason, setOtherReason] = useState('');

  useEffect(() => {
    if (selectedReason) {
      if (selectedReason === 'Outro motivo') {
        if (otherReason.trim().length > 0) {
          setErrorMessage('');
        } else {
          setErrorMessage(
            'Por favor, selecione um motivo para o cancelamento.',
          );
        }
      } else {
        setErrorMessage('');
      }
    }
  }, [selectedReason, otherReason]);

  useEffect(() => {
    if (item?.type?.toLowerCase() === 'compra') {
      setModalMessage(
        'Se houverem produtos bonificados associados a este pedido, eles também serão cancelados!',
      );
    } else if (item?.type?.toLowerCase() === 'bonificação') {
      setModalMessage(
        'Você está cancelando apenas este produto bonificado. Se quiser cancelar o pedido inteiro, vá no pedido com tipo: "Compra".',
      );
    }
  }, [item]);

  const cancel = async () => {
    let isValid = true;
    if (selectedReason) {
      if (selectedReason === 'Outro motivo') {
        isValid = otherReason.trim().length > 0;
      } else {
        isValid = true;
      }
      setErrorMessage('');
    } else {
      isValid = false;
      setErrorMessage('Por favor, selecione um motivo para o cancelamento.');
    }

    if (!isValid) return;

    setLoading(true);
    await OrdersService.CancelOrder(
      clientCode,
      item['@attributes']?.ORDEM,
      selectedReason === 'Outro motivo' ? otherReason : selectedReason,
    )
      .then(res => {
        if (res) {
          logEvent(analyticsInstance, 'order_cancel', {
            client_code: clientCode,
            order_number: item['@attributes']?.ORDEM,
            order_value: parseFloat(item['@attributes']?.VALOR),
          });
          setToast(true);

          setTimeout(() => {
            navigate(AppRoutes.MAIN_TAB_SCREEN, {
              screen: AppRoutes.ORDERS,
              params: {
                force: true,
              },
            });
          }, 1500);
        }
      })
      .catch(err => {
        let errorMessage = 'Erro ao cancelar pedido';
        if (err.response) {
          if (err.response.data) {
            if (err.response.data.message) {
              errorMessage = err.response.data.message;
            }
            if (err.response.data.error) {
              errorMessage = err.response.data.error;
            }
            if (err.response.data.details) {
              errorMessage += `: ${err.response.data.details}`;
            }
          } else if (err.response.status) {
            errorMessage = `Erro HTTP: ${err.response.status}`;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        Alert.alert(errorMessage);
      });
    setLoading(false);
  };

  const confirmCancel = () => {
    setShowModal(false);
    cancel();
  };

  const showAlert = () => {
    if (!modalMessage) {
      setModalMessage('Tem certeza que deseja cancelar o pedido?');
    }
    setShowModal(true);
  };

  return (
    <>
      <Modal
        visible={showModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Confirmação de Cancelamento</Text>
              <Text style={styles.modalMessage}>{modalMessage}</Text>
            </View>
            <View style={styles.modalButtons}>
              <Pressable style={styles.modalButtonConfirm} onPress={confirmCancel}>
                <Text style={styles.modalButtonConfirmText}>Confirmar</Text>
              </Pressable>
              <Pressable style={styles.modalButtonCancel} onPress={() => setShowModal(false)}>
                <Text style={styles.modalButtonCancelText}>Cancelar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
      <KeyboardAvoidingView
      style={{flex: 1}}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      {toast && <Toast title="Pedido de cancelamento enviado com sucesso!" />}
      <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
        <ScrollView
          contentContainerStyle={{flexGrow: 1, paddingHorizontal: 20}}
          keyboardShouldPersistTaps="handled">
          <Header />
          <View style={styles.containerHeader}>
            <SearchHeader onPress={goBack} title="Cancelar pedido" />
          </View>
          <TemporaryPasswordBanner />

          {loading && (
            <ActivityIndicator color={Colors.RedPallete.red} size={35} />
          )}

          {canCancel ? (
            <>
              <View style={styles.content}>
                <Text style={styles.text}>
                  Lamentamos que você queira cancelar seu pedido. Poderia nos
                  informar o motivo? Seu feedback é essencial para melhorarmos
                  sua experiência.
                </Text>
                <Text style={styles.titleDescription}>
                  Por que você quer cancelar o seu pedido?
                </Text>
              </View>

              <FlatList
                data={reasons}
                keyExtractor={(item, index) => index.toString()}
                nestedScrollEnabled={true}
                scrollEnabled={false}
                renderItem={({item}) => (
                  <>
                    <Pressable
                      onPress={() => setSelectedReason(item)}
                      style={styles.radioContainer}>
                      <View style={styles.radio}>
                        {selectedReason === item && (
                          <View style={styles.radioSelected} />
                        )}
                      </View>
                      <Text style={styles.text}>{item}</Text>
                    </Pressable>

                    {selectedReason === 'Outro motivo' &&
                      item === 'Outro motivo' && (
                        <TextInput
                          style={styles.textInput}
                          placeholder="Digite seu motivo"
                          placeholderTextColor="gray"
                          value={otherReason}
                          onChangeText={setOtherReason}
                        />
                      )}
                  </>
                )}
              />

              {errorMessage ? (
                <Text style={styles.errorText}>{errorMessage}</Text>
              ) : null}

              <View style={{marginTop: 'auto', paddingBottom: 20}}>
                <ButtonComponent
                  title="Manter Pedido"
                  width="100%"
                  onPress={goBack}
                />
                <View style={{marginTop: 10}}>
                  <ButtonComponent
                    disabled={!!errorMessage || loading || temporaryPasswordLogin}
                    outlined={true}
                    title="Cancelar Pedido"
                    width="100%"
                    onPress={showAlert}
                  />
                </View>
              </View>
            </>
          ) : null}
        </ScrollView>
      </TouchableWithoutFeedback>
    </KeyboardAvoidingView>
    </>
  );
};

export default CancelOrder;
